<template>
  <div ref="trigger" class="home-resource-trigger" @pointerenter="enter" @pointermove="enter" @pointerleave="hide" @focusin="focus" @focusout="leaveFocus" @click.capture="dismiss">
    <slot />
  </div>
  <Teleport to="body">
    <Transition name="resource-preview">
      <button v-if="open" ref="preview" type="button" class="home-resource-preview" :class="{'is-keyboard':keyboard}" :data-side="placement.side" :style="position" :aria-label="action"
        @pointerenter="keep" @pointerleave="hide" @focus="keep" @blur="hide" @click="activate">
        <span class="home-resource-preview-media" :class="`is-${kind}`">
          <img v-if="kind==='gallery'" :src="galleryImage" width="1200" height="750" alt="" draggable="false" />
          <span v-else-if="kind==='deck'" class="home-deck-preview" aria-hidden="true">
            <img v-for="slide in [9,8,1]" :key="slide" :src="`/assets/deck/cathay-flex/preview-${slide}.webp`" alt="" draggable="false" />
          </span>
          <img v-else :src="workflow.src" :width="workflow.width" :height="workflow.height" alt="" draggable="false" />
        </span>
        <span class="home-resource-preview-caption"><span>{{ title }}</span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 10h13m-5-5 5 5-5 5" /></svg></span>
      </button>
    </Transition>
  </Teleport>
</template>
<script>
import { projectArchitectures } from '../../data/projectArchitectures';
import { supportsDesktopHover, watchDesktopHover } from '../../utils/desktopHover';
import { resourcePreviewPosition } from '../../utils/resourcePreviewPosition';
import { previewDelay, warmPreviewIntent } from '../../utils/previewIntent';
const HOVER = { minWidth: 0 };
export default {
  props: { kind: { type: String, required: true }, active: Boolean, dark: Boolean },
  data: () => ({ open: false, placement: null, keyboard: false }),
  computed: {
    workflow() { return projectArchitectures.slidepoise; },
    galleryImage() { return `/assets/img/projects/gallery-experience/home-preview-${this.dark ? 'dark' : 'light'}.webp`; },
    title() { return { gallery: 'The View Gallery', deck: 'Cathay Flex · Pitch deck', workflow: 'SlidePoise · Workflow' }[this.kind]; },
    action() { return { gallery: 'Enter the Gallery', deck: 'View pitch deck', workflow: 'View SlidePoise workflow' }[this.kind]; },
    position() { const p = this.placement; return p && { left: `${p.left}px`, top: `${p.top}px`, width: `${p.width}px`, height: `${p.height}px`, '--resource-origin': p.origin }; },
  },
  mounted() {
    this.warmGallery();
    this._stopHover = watchDesktopHover(enabled => { if (!enabled) this.dismiss(); }, HOVER);
    this._escape = event => { if (event.key === 'Escape') { const restore = this.$refs.preview === document.activeElement; this.dismiss(); if (restore) { this._restoring = true; this._control?.focus({ preventScroll: true }); this._restoring = false; } } };
    window.addEventListener('keydown', this._escape);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.dismiss, { passive: true });
  },
  beforeUnmount() { this.dismiss(); this._stopHover?.(); window.removeEventListener('keydown', this._escape); window.removeEventListener('scroll', this.onScroll); window.removeEventListener('resize', this.dismiss); },
  deactivated() { this.dismiss(); },
  watch: {
    active(value) {
      if (!value) this.dismiss();
      else {
        this.warmGallery();
        this.$nextTick(() => { if (this.$refs.trigger?.matches(':hover')) this.enter({ pointerType: 'mouse' }); });
      }
    },
    galleryImage() { this.warmGallery(); },
    '$route.fullPath'() { this.dismiss(); },
  },
  methods: {
    warmGallery() { if (this.active && this.kind === 'gallery') { const image = new Image(); image.src = this.galleryImage; image.decode().catch(() => {}); } },
    enter(event) {
      if (!this.active || event.pointerType === 'touch' || !supportsDesktopHover(HOVER)) return;
      clearTimeout(this._hide);
      if (this.open || this._show) return;
      this._show = setTimeout(() => { this._show = null; this.show(false); }, previewDelay());
    },
    focus(event) { if (!this._restoring && event.target.matches(':focus-visible')) this.show(true); },
    show(keyboard) {
      if (!this.active) return;
      this.keep();
      this._control = this.$refs.trigger?.querySelector('a,button');
      if (!this._control) return;
      const rect = this._control.getBoundingClientRect();
      const header = document.querySelector('.header')?.getBoundingClientRect().bottom || 0;
      this.placement = resourcePreviewPosition(rect, { width: innerWidth, height: innerHeight, topInset: Math.max(12, header < innerHeight / 3 ? header + 8 : 12) }, this.kind === 'workflow' ? 440 : 380, this.kind === 'workflow' ? 1600 / 940 : 1.6);
      this.keyboard = keyboard;
      this.open = Boolean(this.placement);
      if(this.open)warmPreviewIntent();
    },
    keep() { clearTimeout(this._show); this._show = null; clearTimeout(this._hide); },
    hide() { this.keep(); if(this.open)warmPreviewIntent(); this._hide = setTimeout(() => { this.open = false; }, 180); },
    leaveFocus(event) { if (this.$refs.trigger && !this.$refs.trigger.contains(event.relatedTarget) && event.relatedTarget !== this.$refs.preview) this.hide(); },
    onScroll() {
      // Project selection also scrolls the pinned chapter. Preserve pointer
      // intent through that movement, including an opening delay already running.
      const focused = this.keyboard && this.$refs.trigger?.contains(document.activeElement);
      const hovered = supportsDesktopHover(HOVER) && (this.$refs.trigger?.matches(':hover') || this.$refs.preview?.matches(':hover'));
      if (this.active && (focused || hovered)) {
        if (this.open) this.show(Boolean(focused));
        return;
      }
      this.dismiss();
    },
    dismiss() { this.keep(); this.open = false; },
    activate() { const control = this._control; this.dismiss(); control?.click(); },
  },
};
</script>
<style scoped>
.home-resource-trigger{display:block;width:fit-content;max-width:100%}
.home-resource-preview{--resource-paper:255,255,255;--resource-ink:#353535;--resource-edge:rgba(255,255,255,.8);position:fixed;z-index:1000;display:flex;flex-direction:column;box-sizing:border-box;padding:7px;border:1px solid var(--resource-edge);border-radius:14px;background:rgba(var(--resource-paper),.78);backdrop-filter:blur(24px) saturate(.8);-webkit-backdrop-filter:blur(24px) saturate(.8);color:var(--resource-ink);box-shadow:0 0 0 1px #77777722,0 18px 48px -16px #00000038,inset 0 1px 0 #ffffff30;text-align:left;cursor:var(--site-interactive-cursor,pointer);transform-origin:var(--resource-origin)}
.home-resource-preview-media{position:relative;display:block;flex:1;min-height:0;width:100%;border-radius:7px;overflow:hidden;background:rgba(var(--resource-paper),.38)}
.home-resource-preview-media>img{display:block;width:100%;height:100%;object-fit:contain}
.home-resource-preview-caption{display:flex;align-items:center;justify-content:space-between;gap:12px;flex:none;height:42px;padding:0 9px;font:400 14px/1.35 'Quattrocento Sans',sans-serif}
.home-resource-preview-caption svg{width:17px;height:17px;flex:none;fill:none;stroke:currentColor;stroke-width:1.2;transition:transform 180ms ease;opacity:.65}
.home-resource-preview:focus-visible{outline:2px solid currentColor;outline-offset:4px}
.home-resource-preview:active{transform:scale(.99)}
.home-deck-preview{position:absolute;inset:0;display:block;perspective:800px}
.home-deck-preview img{position:absolute;width:78%;height:auto;border:1px solid #0000000b;box-shadow:0 6px 16px #00000016;left:5%;top:9%;transform-origin:center;transition:transform 240ms cubic-bezier(.22,1,.36,1)}
.home-deck-preview img:nth-child(1){left:17%;top:9%;transform:rotate(5deg)}
.home-deck-preview img:nth-child(2){left:11%;top:20%;transform:rotate(1deg)}
.home-deck-preview img:nth-child(3){top:32%;transform:rotate(-3deg)}
@media(hover:hover) and (pointer:fine){.home-resource-preview:hover .home-resource-preview-caption svg{transform:translateX(3px);opacity:1}.home-resource-preview:hover .home-deck-preview img:nth-child(1){transform:translateY(-3px) rotate(7deg)}.home-resource-preview:hover .home-deck-preview img:nth-child(3){transform:translateY(2px) rotate(-4deg)}}
:global(body.dark-mode .home-resource-preview){--resource-paper:32,32,34;--resource-ink:#eee;--resource-edge:rgba(255,255,255,.19);box-shadow:0 0 0 1px #00000040,0 18px 48px -16px #00000080,inset 0 1px 0 #ffffff12}
.resource-preview-enter-active{transition:opacity 160ms ease,transform 220ms cubic-bezier(.22,1,.36,1)}
.resource-preview-leave-active{transition:opacity 130ms ease,transform 150ms ease}
.resource-preview-enter-from,.resource-preview-leave-to{opacity:0;transform:scale(.97)}
.home-resource-preview.is-keyboard{transition:none}
@media(max-width:760px){.home-resource-trigger{display:inline-block;vertical-align:middle}}
@media(prefers-reduced-transparency:reduce),(prefers-contrast:more){.home-resource-preview{background:rgb(var(--resource-paper));backdrop-filter:none;-webkit-backdrop-filter:none}}
@media(prefers-reduced-motion:reduce){.home-resource-preview,.home-resource-preview *{transition:none!important;transform:none!important}}
</style>
